import React, { useState, useEffect } from 'react';
import { 
  CurrencyDollarIcon, 
  ChartBarIcon, 
  UsersIcon, 
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  Squares2X2Icon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useWeb3 } from '../../contexts/Web3Context';
import RevenueChart from './RevenueChart';
import RevenueDistributionHistory from './RevenueDistributionHistory';

interface RevenueData {
  workId: string;
  workTitle: string;
  totalRevenue: string;
  nftSales: number;
  collaborators: {
    name: string;
    address: string;
    sharePercentage: number;
    earnedAmount: string;
  }[];
  salesHistory: {
    date: number;
    amount: string;
    currency: string;
    buyer: string;
    type: 'nft_sale' | 'streaming' | 'licensing';
  }[];
  priceHistory: {
    date: number;
    price: string;
    volume: number;
  }[];
}

interface NFTInvestmentData {
  tokenId: string;
  workTitle: string;
  originalPrice: string;
  currentPrice: string;
  priceChange: number;
  purchaseDate: number;
  owner: string;
  ownerName: string;
  isEarlyInvestor: boolean;
  roi: number; // 投资回报率
}

const RevenueAnalytics: React.FC = () => {
  const { wallet } = useWeb3();
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [investmentData, setInvestmentData] = useState<NFTInvestmentData[]>([]);
  const [selectedWork, setSelectedWork] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'revenue' | 'investment' | 'distribution'>('revenue');
  const [chartType, setChartType] = useState<'line' | 'area' | 'bar'>('area');
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  useEffect(() => {
    if (wallet?.address) {
      loadRevenueData();
    }
  }, [wallet]);

  const loadRevenueData = async () => {
    if (!wallet?.address) return;
    
    setLoading(true);
    try {
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      
      // 获取音乐作品NFT数据
      const musicWorks = mockData.getMusicWorkNFTs(wallet.address);
      const revenueHistory = mockData.getRevenueHistory(wallet.address);
      
      // 将统一mock数据转换为收益分析格式
      const mockRevenueData: RevenueData[] = musicWorks.map((work) => {
        const totalEarned = work.revenueShares.reduce((sum, share) => sum + share.totalEarned, 0);
        
        return {
          workId: work.workId,
          workTitle: work.title,
          totalRevenue: totalEarned.toString(),
          nftSales: work.mintedCount,
          collaborators: work.revenueShares.map(share => ({
            name: share.participantName,
            address: share.participantAddress,
            sharePercentage: share.sharePercentage,
            earnedAmount: share.totalEarned.toString()
          })),
          salesHistory: work.transactions.map(tx => ({
            date: tx.timestamp,
            amount: tx.price?.toString() || '0',
            currency: tx.currency,
            buyer: tx.to,
            type: tx.type as 'nft_sale' | 'streaming' | 'licensing'
          })),
          priceHistory: [
            { date: work.createdAt - 86400000 * 7, price: (work.floorPrice * 0.6).toString(), volume: 1 },
            { date: work.createdAt - 86400000 * 5, price: (work.floorPrice * 0.8).toString(), volume: 2 },
            { date: work.createdAt - 86400000 * 3, price: (work.floorPrice * 0.9).toString(), volume: 1 },
            { date: work.createdAt - 86400000, price: work.floorPrice.toString(), volume: 3 }
          ]
        };
      }).filter(work => parseFloat(work.totalRevenue) > 0);

      // 基于统一mock数据生成NFT投资数据
      const mockInvestmentData: NFTInvestmentData[] = musicWorks.filter(work => work.isOnChain).map((work, index) => {
        const originalPrice = work.floorPrice * 0.4;
        const priceChange = ((work.floorPrice - originalPrice) / originalPrice) * 100;
        
        return {
          tokenId: work.tokenId || `nft_${index + 1}`,
          workTitle: `${work.title} #${index + 1}`,
          originalPrice: originalPrice.toString(),
          currentPrice: work.floorPrice.toString(),
          priceChange: priceChange,
          purchaseDate: work.createdAt - 86400000 * 30,
          owner: wallet.address,
          ownerName: '当前用户',
          isEarlyInvestor: index < 2,
          roi: priceChange
        };
      });

      setRevenueData(mockRevenueData);
      setInvestmentData(mockInvestmentData);
      
    } catch (error) {
      console.error('Failed to load revenue data:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount: string) => {
    return `${parseFloat(amount).toFixed(2)} SOL`;
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // 生成图表数据
  const generateChartData = () => {
    const dates = [];
    const now = new Date();
    
    // 生成过去30天的数据
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      const baseRevenue = Math.random() * 50 + 20;
      const nftSales = Math.random() * 30 + 10;
      const royalties = Math.random() * 20 + 5;
      const streaming = Math.random() * 15 + 3;
      
      dates.push({
        date: date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
        totalRevenue: baseRevenue + nftSales + royalties + streaming,
        nftSales,
        royalties,
        streaming
      });
    }
    
    return dates;
  };

  // 生成收益分布数据
  const generateDistributionData = () => {
    return [
      { name: 'NFT销售', value: 45.2 },
      { name: '版税收入', value: 28.8 },
      { name: '流媒体', value: 16.5 },
      { name: '授权费用', value: 9.5 }
    ];
  };

  const RevenueTab = () => (
    <div className="space-y-6">
      {/* 图表控制栏 */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">视图模式:</span>
              <div className="flex rounded-lg border border-gray-300">
                <button
                  onClick={() => setViewMode('chart')}
                  className={`px-3 py-1 text-sm rounded-l-lg ${
                    viewMode === 'chart'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ChartBarIcon className="w-4 h-4 inline mr-1" />
                  图表
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 text-sm rounded-r-lg ${
                    viewMode === 'table'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Squares2X2Icon className="w-4 h-4 inline mr-1" />
                  表格
                </button>
              </div>
            </div>
            
            {viewMode === 'chart' && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">图表类型:</span>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as 'line' | 'area' | 'bar')}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="area">面积图</option>
                  <option value="line">折线图</option>
                  <option value="bar">柱状图</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 收益概览 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <CurrencyDollarIcon className="w-8 h-8 text-green-500" />
            <div>
              <h3 className="text-sm text-gray-500">总收益</h3>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(revenueData.reduce((sum, work) => sum + parseFloat(work.totalRevenue), 0).toString())}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="w-8 h-8 text-blue-500" />
            <div>
              <h3 className="text-sm text-gray-500">NFT销售数量</h3>
              <p className="text-2xl font-bold text-blue-600">
                {revenueData.reduce((sum, work) => sum + work.nftSales, 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-purple-500" />
            <div>
              <h3 className="text-sm text-gray-500">协作作品</h3>
              <p className="text-2xl font-bold text-purple-600">{revenueData.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <ArrowTrendingUpIcon className="w-8 h-8 text-orange-500" />
            <div>
              <h3 className="text-sm text-gray-500">平均增值</h3>
              <p className="text-2xl font-bold text-orange-600">+145%</p>
            </div>
          </div>
        </div>
      </div>

      {/* 收益趋势图表 */}
      {viewMode === 'chart' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">收益趋势分析</h3>
              <p className="text-sm text-gray-600">过去30天的收益变化趋势</p>
            </div>
            <RevenueChart data={generateChartData()} chartType={chartType} />
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">收益分布</h3>
              <p className="text-sm text-gray-600">各类收入来源占比</p>
            </div>
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <h4 className="text-lg font-medium text-gray-900 mb-4">收益分布</h4>
                <div className="grid grid-cols-2 gap-4">
                  {generateDistributionData().map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{item.value}%</div>
                      <div className="text-sm text-gray-600">{item.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 作品收益列表 */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">协作作品收益统计</h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {revenueData.map((work) => (
            <div 
              key={work.workId} 
              className="p-6 hover:bg-gray-50 cursor-pointer"
              onClick={() => setSelectedWork(work)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">{work.workTitle}</h3>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(work.totalRevenue)}</p>
                  <p className="text-sm text-gray-500">{work.nftSales} NFT销售</p>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">协作者分成明细：</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {work.collaborators.map((collab, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{collab.name}</span>
                        <span className="text-sm text-blue-600">{collab.sharePercentage}%</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500 font-mono">
                          {collab.address.substring(0, 10)}...
                        </span>
                        <span className="text-sm font-bold text-green-600">
                          {formatCurrency(collab.earnedAmount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-sm text-gray-500">
                最新销售: {work.salesHistory.length > 0 ? 
                  new Date(work.salesHistory[0].date).toLocaleDateString() : '暂无'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const DistributionTab = () => {
    const [distributionData, setDistributionData] = useState<any[]>([]);

    useEffect(() => {
      const loadDistributionData = async () => {
        try {
          const { RevenueMockData } = await import('../../data/mock/revenueData');
          const distributions = RevenueMockData.getRevenueDistributionHistory();
          setDistributionData(distributions);
        } catch (error) {
          console.error('Failed to load distribution data:', error);
        }
      };
      
      loadDistributionData();
    }, []);

    return <RevenueDistributionHistory distributions={distributionData} />;
  };

  const InvestmentTab = () => (
    <div className="space-y-6">
      {/* 早期投资价值提示 */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 p-6 rounded-lg">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <ArrowTrendingUpIcon className="w-8 h-8 text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">💎 早期投资价值机会</h3>
            <p className="text-yellow-700 mb-3">
              <strong>早期购买音乐NFT具有巨大投资价值！</strong>随着艺术家知名度提升和作品流行度增加，NFT价值可能显著增长。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/50 rounded-lg p-3">
                <div className="font-semibold text-yellow-800">🚀 增值潜力</div>
                <div className="text-yellow-700">平均增值 +171%</div>
              </div>
              <div className="bg-white/50 rounded-lg p-3">
                <div className="font-semibold text-yellow-800">⏰ 最佳时机</div>
                <div className="text-yellow-700">作品发布初期</div>
              </div>
              <div className="bg-white/50 rounded-lg p-3">
                <div className="font-semibold text-yellow-800">💰 收益来源</div>
                <div className="text-yellow-700">价值增长 + 版税分红</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 投资概览 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <ArrowTrendingUpIcon className="w-8 h-8 text-green-500" />
            <div>
              <h3 className="text-sm text-gray-500">早期投资者数量</h3>
              <p className="text-2xl font-bold text-green-600">
                {investmentData.filter(nft => nft.isEarlyInvestor).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <CurrencyDollarIcon className="w-8 h-8 text-blue-500" />
            <div>
              <h3 className="text-sm text-gray-500">早期投资者平均ROI</h3>
              <p className="text-2xl font-bold text-blue-600">
                {formatPercentage(investmentData.filter(nft => nft.isEarlyInvestor).reduce((sum, nft) => sum + nft.roi, 0) / investmentData.filter(nft => nft.isEarlyInvestor).length)}
              </p>
              <p className="text-xs text-green-600 mt-1">🔥 远超市场平均</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="w-8 h-8 text-purple-500" />
            <div>
              <h3 className="text-sm text-gray-500">增值NFT数量</h3>
              <p className="text-2xl font-bold text-purple-600">
                {investmentData.filter(nft => nft.priceChange > 0).length}
              </p>
              <p className="text-xs text-purple-600 mt-1">📈 {((investmentData.filter(nft => nft.priceChange > 0).length / investmentData.length) * 100).toFixed(0)}% 成功率</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-md p-6 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">💎</span>
            </div>
            <div>
              <h3 className="text-sm text-green-700 font-medium">最高收益记录</h3>
              <p className="text-2xl font-bold text-green-600">+280%</p>
              <p className="text-xs text-green-600 mt-1">⭐ 早期投资者专享</p>
            </div>
          </div>
        </div>
      </div>

      {/* NFT投资价值列表 */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">NFT投资价值追踪</h2>
          <p className="text-sm text-gray-600 mt-1">早期购买者的投资回报情况</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {investmentData.map((nft) => (
            <div key={nft.tokenId} className={`p-6 ${nft.isEarlyInvestor ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : ''}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium">{nft.workTitle}</h3>
                    {nft.isEarlyInvestor && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs rounded-full font-medium">
                          💎 早期投资者
                        </span>
                        {nft.priceChange > 150 && (
                          <span className="inline-block px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                            🚀 超高收益
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">持有者: {nft.ownerName}</p>
                  {nft.isEarlyInvestor && (
                    <p className="text-xs text-yellow-700 mt-1">
                      ⭐ 早期发现价值，获得丰厚回报
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    {nft.priceChange > 0 ? (
                      <ArrowUpIcon className="w-6 h-6 text-green-500" />
                    ) : (
                      <ArrowDownIcon className="w-6 h-6 text-red-500" />
                    )}
                    <span className={`text-2xl font-bold ${
                      nft.priceChange > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatPercentage(nft.priceChange)}
                    </span>
                  </div>
                  {nft.isEarlyInvestor && nft.priceChange > 0 && (
                    <p className="text-xs text-green-600">💰 投资眼光独到</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">原始价格:</span>
                  <p className="font-medium">{formatCurrency(nft.originalPrice)}</p>
                </div>
                <div>
                  <span className="text-gray-500">当前价格:</span>
                  <p className="font-medium">{formatCurrency(nft.currentPrice)}</p>
                </div>
                <div>
                  <span className="text-gray-500">购买时间:</span>
                  <p className="font-medium">{new Date(nft.purchaseDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">投资回报:</span>
                  <p className={`font-bold ${nft.roi > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPercentage(nft.roi)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以查看收益分析</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">收益与投资分析</h1>
        <p className="text-gray-600">协作作品版权收益统计与NFT投资价值追踪</p>
      </div>

      {/* 标签页切换 */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('revenue')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'revenue'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5" />
                协作收益统计
              </div>
            </button>
            <button
              onClick={() => setActiveTab('investment')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'investment'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ArrowTrendingUpIcon className="w-5 h-5" />
                NFT投资价值
              </div>
            </button>
            <button
              onClick={() => setActiveTab('distribution')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'distribution'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5" />
                收益分配历史
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* 内容区域 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">加载中...</p>
        </div>
      ) : (
        <>
          {activeTab === 'revenue' && <RevenueTab />}
          {activeTab === 'investment' && <InvestmentTab />}
          {activeTab === 'distribution' && <DistributionTab />}
        </>
      )}
    </div>
  );
};

export default RevenueAnalytics;
